export type BingoCell = {
  letter: string;
  number: number | null; // null for FREE
  id: string;
};

export const COLUMNS = ['B', 'I', 'N', 'G', 'O'] as const;
export type Column = typeof COLUMNS[number];

export const RANGES: Record<Column, [number, number]> = {
  B: [1, 15],
  I: [16, 30],
  N: [31, 45],
  G: [46, 60],
  O: [61, 75],
};

// Generate a standard bingo card
 export function generateBingoCard(): BingoCell[][] {
  const card: BingoCell[][] = [];

  for (let col = 0; col < 5; col++) {
    const letter = COLUMNS[col];
    const [min, max] = RANGES[letter];
    const numbers: number[] = [];
    while (numbers.length < 5) {
      const num = Math.floor(Math.random() * (max - min + 1)) + min;
      if (!numbers.includes(num)) numbers.push(num);
    }
    const columnCells: BingoCell[] = numbers.map((num, row) => ({
      letter,
      number: (col === 2 && row === 2) ? null : num, // FREE in center N
      id: `${letter}${num || 'FREE'}`,
    }));
    card.push(columnCells);
  }

  // Transpose to row-major for easier rendering (5 rows x 5 cols)
  const transposed: BingoCell[][] = Array.from({ length: 5 }, (_, row) =>
    card.map(col => col[row])
  );
  return transposed;
}

export function getColumnForNumber(num: number): Column {
  if (num <= 15) return 'B';
  if (num <= 30) return 'I';
  if (num <= 45) return 'N';
  if (num <= 60) return 'G';
  return 'O';
}

export function checkForBingo(marked: Set<string>, card: BingoCell[][]): boolean {
  // Check rows
  for (let r = 0; r < 5; r++) {
    if (card[r].every(cell => marked.has(cell.id))) return true;
  }
  // Check columns
  for (let c = 0; c < 5; c++) {
    if (card.every(row => marked.has(row[c].id))) return true;
  }
  // Diagonals
  const mainDiag = card.every((row, i) => marked.has(row[i].id));
  const antiDiag = card.every((row, i) => marked.has(row[4 - i].id));
  return mainDiag || antiDiag;
}

export function isBlackout(marked: Set<string>, card: BingoCell[][]): boolean {
  return card.flat().every(cell => marked.has(cell.id));
}
